<template>
  <div id="app">
    <AppLayout>
      <router-view/>
    </AppLayout>
  </div>
</template>

<script>
import AppLayout from '@/components/layout/AppLayout.vue';
import { useHead } from '@vueuse/head';

export default {
  name: 'App',
  components: {
    AppLayout,
  },
  setup() {
    // Function to update favicon based on color scheme
    const updateFavicon = () => {
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const faviconElement = document.getElementById('favicon');
      
      if (faviconElement) {
        faviconElement.href = isDarkMode ? '/img/360_icon_white.ico' : '/img/360_icon_black.ico';
      }
    };

    // Update favicon when the page loads
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateFavicon);
    } else {
      updateFavicon();
    }
    
    // Listen for changes in color scheme
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateFavicon);

    useHead({
      title: '360° Collective - Creative Production House',
      meta: [
        {
          name: 'description',
          content: '360° Collective is a production house with a team of multidisciplinary experts from diverse backgrounds. With years of international experience in major events, creative producing, and production design, our goal is to set new industry standards.'
        },
        {
          name: 'keywords',
          content: 'creative production, agency, shows, major event, ceremonies, immersive experiences, production company, creative agency'
        },
        {
          property: 'og:title',
          content: '360° Collective - Imagineering Experiences'
        },
        {
          property: 'og:description',
          content: '360° Collective is a creative production house specializing in innovative and immersive experiences.'
        },
        {
          property: 'og:type',
          content: 'website'
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: '360° Collective - Imagineering Experiences'
        },
        {
          name: 'twitter:description',
          content: '360° Collective is a creative production house specializing in innovative and immersive experiences.'
        }
      ],
      link: [
        {
          rel: 'icon',
          type: 'image/x-icon',
          href: '/img/360_icon_black.ico',
          id: 'favicon'
        }
      ]
    });
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/main.scss';

/* Additional global styles from the template */
.section-header {
  font-size: 120px;
  font-weight: 700;
  margin-bottom: 64px;
  text-transform: uppercase;
  line-height: 1;
  
  @media (max-width: 1024px) {
    font-size: 80px;
  }
  
  @media (max-width: 768px) {
    font-size: 64px;
    margin-bottom: 40px;
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Grid layouts */
.projects-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.about-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

/* Add this at the end of your existing styles */
body.nav-open {
  overflow: hidden;
  height: 100vh;
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
}

/* Ensure we hide the default cursor on all elements */
html, body, a, button, input, select, textarea, [role="button"] {
  &.no-cursor {
    cursor: none !important;
  }
}

/* For touch devices, restore normal cursor behavior */
@media (hover: none) {
  html, body, a, button, input, select, textarea, [role="button"] {
    cursor: auto !important;
  }
}

/* Cursor styles */
:root {
  --black: #000;
  --white: #fff;
  --accent: #c9963e;
}

/* Better mobile detection */
body.no-cursor {
  cursor: none !important;
}
</style>
